import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type { GamePassUserProgressResponse } from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";
import { normalizeUserProgressResponse } from "./userProgressMappers";

const buildUserProgressUrl = (userId: string): string =>
  API_ROUTES.GET_GAME_PASS_USER_PROGRESS.replace("{user_id}", userId);

const fetchUserProgress = async (
  userId: string,
  seasonId?: string,
): Promise<GamePassUserProgressResponse> => {
  const response = await getPrivateInstance().get<GamePassUserProgressResponse>(
    buildUserProgressUrl(userId),
    { params: seasonId ? { season_id: seasonId } : undefined },
  );
  return normalizeUserProgressResponse(response.data);
};

type UseGetUserProgressOptions = {
  enabled?: boolean;
};

/**
 * `GET /gamepass/v2/admin/users/{user_id}/progress?season_id=…` (normalized).
 *
 * Season-only on purpose: Monthly Challenges use
 * `useGetGamePassUserCampaignProgress`, which returns the RAW envelope under
 * the `"campaign"`-discriminated key. Accepting a campaignId here would cache
 * a normalized shape under that same key and the two hooks would silently
 * hand each other the wrong shape.
 */
export const useGetUserProgress = (
  userId?: string,
  seasonId?: string,
  options?: UseGetUserProgressOptions,
): UseQueryResult<GamePassUserProgressResponse, Error> =>
  useQuery<GamePassUserProgressResponse, Error>({
    // The `"season"` segment discriminates from campaign progress — monthly
    // campaigns reuse the same uuid as `season_id` on enrollments, and a
    // shared trailing id made React Query reuse a `?season_id=` response for
    // `?campaign_id=` (FE-2544).
    queryKey: [
      "game-pass",
      "users",
      "progress",
      userId ?? "",
      "season",
      seasonId ?? "",
    ],
    queryFn: () => fetchUserProgress(userId!, seasonId),
    enabled: Boolean(userId && seasonId) && (options?.enabled ?? true),
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
  });

export { fetchUserProgress };
